import type { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { locateSpan, type ILocatableSpan } from './span-locator'

/**
 * Applying a promotion edits the author's TEXT — it never adds a rule directly. Compiled rules
 * are re-derived from the body on every compile, so a binding introduced any other way would
 * evaporate on the next Analyze.
 */

/** Sentence terminators, for avoiding a doubled one when the replacement supplies its own. */
const TERMINATORS = new Set(['.', '!', '?'])

/**
 * The body with `replacement` spliced in, or null when the span can no longer be located. Two
 * adjustments avoid obviously-broken output: a following terminator is consumed since the
 * replacement brings its own, and the first letter is lowercased mid-sentence.
 *
 * Deliberately no cleverer. The result goes into the visible textarea for the author to read
 * before Analyze — an awkward join is something they can see and fix, where a rewrite that
 * smoothed their meaning away is not.
 */
export function applyReplacement(
    body: string,
    replaces: ILocatableSpan,
    replacement: string,
    compiledBody?: string,
): string | null {
    const located = locateSpan(body, replaces, compiledBody)
    if (located === null) return null

    let end = located.end
    if (TERMINATORS.has(replacement.slice(-1)) && TERMINATORS.has(body.charAt(end))) end += 1

    const preceding = body.slice(0, located.start)
    const precedingText = preceding.trimEnd()
    // A line break ends the sentence as surely as a period, and directives are full of
    // statements written one per line with no terminating punctuation. Deciding from `trimEnd()`
    // alone discards the newline that settles it, then lowercases the start of its own line.
    const brokeLine = preceding.length > precedingText.length && /[\r\n]/.test(preceding.slice(precedingText.length))
    const midSentence = precedingText !== '' && !brokeLine && !TERMINATORS.has(precedingText.slice(-1))
    const text = midSentence ? replacement.charAt(0).toLowerCase() + replacement.slice(1) : replacement

    return body.slice(0, located.start) + text + body.slice(end)
}

/**
 * Grouped by the statement they are about, preserving server order. A statement naming a length
 * but no field ("Be punchy") yields one suggestion per plausible field sharing an `item_text`.
 * They are alternatives — rendered separately they would read as "apply both", putting two hard
 * limits on the author for one sentence.
 */
export function groupSuggestionsByItem(
    suggestions: DirectivePromotionSuggestion[],
): Map<string, DirectivePromotionSuggestion[]> {
    const grouped = new Map<string, DirectivePromotionSuggestion[]>()

    for (const suggestion of suggestions) {
        const group = grouped.get(suggestion.item_text)
        if (group) group.push(suggestion)
        else grouped.set(suggestion.item_text, [suggestion])
    }

    return grouped
}

/**
 * The four length dimensions a suggestion can target. Mirrors `AI_DIRECTIVE_BINDINGS_V1` rather
 * than deriving from it: filtering the catalog by naming conventions in the ids is a weaker
 * guarantee than four explicit rows, and most of the catalog is not a length at all.
 *
 * The unit is part of the identity, not decoration: 62 characters and 62 words are wildly
 * different rules, and the author is committing to one of them.
 */
export interface IPromotionDimension {
    bindingId: string
    attribute: string
    field: 'title' | 'body'
    unit: 'characters' | 'words'
    /** Plural, sentence-leading — "Titles must stay under 62 characters." */
    fieldLabel: string
}

export const PROMOTION_DIMENSIONS: IPromotionDimension[] = [
    {
        bindingId: 'title_max_length',
        attribute: 'title.charLength',
        field: 'title',
        unit: 'characters',
        fieldLabel: 'Titles',
    },
    {
        bindingId: 'title_max_words',
        attribute: 'title.wordLength',
        field: 'title',
        unit: 'words',
        fieldLabel: 'Titles',
    },
    {
        bindingId: 'body_max_length',
        attribute: 'body.charLength',
        field: 'body',
        unit: 'characters',
        fieldLabel: 'Bodies',
    },
    { bindingId: 'body_max_words', attribute: 'body.wordLength', field: 'body', unit: 'words', fieldLabel: 'Bodies' },
]

export function dimensionByBindingId(bindingId: string): IPromotionDimension | undefined {
    return PROMOTION_DIMENSIONS.find((d) => d.bindingId === bindingId)
}

export function dimensionFor(field: 'title' | 'body', unit: 'characters' | 'words'): IPromotionDimension {
    return PROMOTION_DIMENSIONS.find((d) => d.field === field && d.unit === unit)!
}

/**
 * The sentence that goes into the author's text.
 *
 * Built here rather than taken from the server's `suggested_text` because the author can change
 * the field, the unit and the number before confirming — at which point the server's sentence
 * describes a rule they rejected. One builder owning every author-visible wording is the point:
 * two of them, one for the untouched suggestion and one for an adjusted one, is precisely how a
 * preview ends up disagreeing with what actually gets inserted.
 *
 * Kept word-for-word identical to sailfish's own `replacementSentence`, so an untouched
 * suggestion inserts exactly the sentence the server proposed.
 */
export function promotionSentence(dimension: IPromotionDimension, value: number): string {
    return `${dimension.fieldLabel} must stay under ${value} ${dimension.unit}.`
}

/**
 * The one-line justification for a limit on `dimension`, or null when there is no measurement
 * behind it.
 *
 * Null is a real answer, not a gap to fill with a plausible sentence: the author may retarget a
 * suggestion onto a dimension the corpus never exercised (a domain that never writes bodies),
 * and inventing a reason for a number nobody measured is worse than admitting there isn't one.
 */
export function promotionRationale(
    dimension: IPromotionDimension,
    p90ByAttribute: Record<string, number>,
    p50ByAttribute: Record<string, number>,
    sampleSize: number,
): string | null {
    const p90 = p90ByAttribute?.[dimension.attribute]
    if (!p90) return null

    const p50 = p50ByAttribute?.[dimension.attribute]
    const field = dimension.field === 'title' ? 'titles' : 'bodies'
    const ceiling = `90% are under ${p90} ${dimension.unit} (${sampleSize} notifications)`

    // The median leads. The ceiling explains where the suggested value came from, but on its own
    // it reads as a recommendation — and p90 recommends almost nothing, since by construction it
    // permits nine tenths of everything already sent. An author who wrote "write short sentences"
    // and is offered "under 99 characters" needs to see that 48 is typical before they can judge
    // whether 99 is the rule they meant.
    return p50
        ? `Your ${field} typically run ${p50} ${dimension.unit}, and ${ceiling}.`
        : `Of your ${field} from the last 90 days, ${ceiling}.`
}
